/**
 * MissionFormModal - Create/edit mission with trajet builder
 */
import React, { useState, useEffect } from 'react';
import { X, Save, UserPlus, Download } from 'lucide-react';
import { missionAPI } from '../../../api/missions.js';
import { driverAPI } from '../../../api/drivers.js';
import { workingSiteAPI } from '../../../api/admin.js';
import TrajetBuilder from './TrajetBuilder.jsx';

export default function MissionFormModal({ mission = null, onClose, onSaved }) {
  const isEdit = !!mission;
  const [form, setForm] = useState({
    title: mission?.title || '',
    description: mission?.description || '',
    driver_id: mission?.driver_id || '',
    vehicle_id: mission?.vehicle_id || '',
    client_name: mission?.client_name || '',
    client_address: mission?.client_address || '',
    departure_site_id: mission?.departure_site_id || '',
    return_site_id: mission?.return_site_id || '',
    planned_start: mission?.planned_start ? mission.planned_start.slice(0, 16) : '',
    planned_end: mission?.planned_end ? mission.planned_end.slice(0, 16) : '',
    priority: mission?.priority || 'normal',
    notes: mission?.notes || '',
  });
  const [trajets, setTrajets] = useState(mission?.trajets || []);
  const [drivers, setDrivers] = useState([]);
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadLookups();
  }, []);

  const loadLookups = async () => {
    try {
      const [drRes, siteRes] = await Promise.all([
        driverAPI.getAll(),
        workingSiteAPI.getAll()
      ]);
      setDrivers(drRes.data || []);
      setSites(siteRes.data || []);
    } catch (err) {
      console.error('Failed to load lookups:', err);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      if (!form.title.trim()) {
        setError('Le titre est obligatoire');
        return;
      }

      const payload = {
        ...form,
        driver_id: form.driver_id ? parseInt(form.driver_id) : null,
        vehicle_id: form.vehicle_id ? parseInt(form.vehicle_id) : null,
        departure_site_id: form.departure_site_id ? parseInt(form.departure_site_id) : null,
        return_site_id: form.return_site_id ? parseInt(form.return_site_id) : null,
        planned_start: form.planned_start || null,
        planned_end: form.planned_end || null,
      };

      let savedMission;
      if (isEdit) {
        const res = await missionAPI.update(mission.id, payload);
        savedMission = res.data;
      } else {
        const res = await missionAPI.create(payload);
        savedMission = res.data;
      }

      // Save trajets for new missions
      if (!isEdit && savedMission?.id) {
        for (const t of trajets) {
          if (t.from_label || t.to_label) {
            await missionAPI.addTrajet(savedMission.id, {
              from_label: t.from_label,
              to_label: t.to_label,
              distance_planned_km: t.distance_planned_km ? parseFloat(t.distance_planned_km) : null,
              purpose: t.purpose || null,
            });
          }
        }
      }

      onSaved(savedMission);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-10 pb-10 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">
            {isEdit ? 'Modifier la mission' : 'Nouvelle mission'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Title + Priority */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Titre *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Ex: Livraison Paris-Lyon"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Priorité</label>
              <select
                value={form.priority}
                onChange={(e) => handleChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="low">Basse</option>
                <option value="normal">Normale</option>
                <option value="high">Haute</option>
                <option value="urgent">Urgente</option>
              </select>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Driver + Client */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Conducteur</label>
              <select
                value={form.driver_id}
                onChange={(e) => handleChange('driver_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Sélectionner --</option>
                {drivers.filter(d => d.status === 'active').map(d => (
                  <option key={d.id} value={d.id}>
                    {d.first_name} {d.last_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Client</label>
              <input
                type="text"
                value={form.client_name}
                onChange={(e) => handleChange('client_name', e.target.value)}
                placeholder="Nom du client"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Client address */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Adresse client</label>
            <input
              type="text"
              value={form.client_address}
              onChange={(e) => handleChange('client_address', e.target.value)}
              placeholder="Adresse de livraison/intervention"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Sites */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Site de départ</label>
              <select
                value={form.departure_site_id}
                onChange={(e) => handleChange('departure_site_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Sélectionner --</option>
                {sites.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Site de retour</label>
              <select
                value={form.return_site_id}
                onChange={(e) => handleChange('return_site_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Sélectionner --</option>
                {sites.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Début planifié</label>
              <input
                type="datetime-local"
                value={form.planned_start}
                onChange={(e) => handleChange('planned_start', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Fin planifiée</label>
              <input
                type="datetime-local"
                value={form.planned_end}
                onChange={(e) => handleChange('planned_end', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes / Instructions</label>
            <textarea
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              rows={2}
              placeholder="Instructions spéciales pour le conducteur..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          {/* Trajet Builder (only for new missions) */}
          {!isEdit && (
            <div className="border-t border-gray-200 pt-4">
              <TrajetBuilder trajets={trajets} onChange={setTrajets} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Enregistrement...' : isEdit ? 'Modifier' : 'Créer la mission'}
          </button>
        </div>
      </div>
    </div>
  );
}
